import { useEffect, lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import PreorderDialog from './components/PreorderDialog';
// Home is loaded eagerly — it is always the entry point.
import Home from './pages/Home';
// All other pages are code-split: only fetched when first navigated to.
const Catalog = lazy(() => import('./pages/Catalog'));
const Collections = lazy(() => import('./pages/Collections'));
const FAQ = lazy(() => import('./pages/FAQ'));
const ShoppingWithUs = lazy(() => import('./pages/ShoppingWithUs'));
const InteriorStyling = lazy(() => import('./pages/InteriorStyling'));
const Showrooms = lazy(() => import('./pages/Showrooms'));
const ReferAFriend = lazy(() => import('./pages/ReferAFriend'));
const Customisation = lazy(() => import('./pages/Customisation'));
const Sustainability = lazy(() => import('./pages/Sustainability'));
const Ambassador = lazy(() => import('./pages/Ambassador'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const Admin = lazy(() => import('./pages/Admin'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const PreorderProductDetail = lazy(() => import('./pages/PreorderProductDetail'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Journal = lazy(() => import('./pages/Journal'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));
const CheckoutCancel = lazy(() => import('./pages/CheckoutCancel'));
const AfterpayReturn = lazy(() => import('./pages/AfterpayReturn'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const NotFound = lazy(() => import('./pages/NotFound'));

import { CartProvider } from './context/CartContext';
import { PreorderProvider } from './context/PreorderContext';
import { WishlistProvider } from './context/WishlistContext';
import { SiteCopyProvider } from './context/SiteCopyContext';
import { Toaster } from './components/ui/toaster';
import ConciergeChat from './components/ConciergeChat';

const PageFallback = () => (
  <div className="min-h-screen bg-[#F5EDDD] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-[#33200F] border-t-transparent rounded-full animate-spin" />
  </div>
);

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer until DOM has mounted the new route's content
      const id = hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [pathname, hash]);
  return null;
};

function App() {
  return (
    <div className="App bg-[#F5EDDD]">
      <HelmetProvider>
        <BrowserRouter>
          <CartProvider>
            <PreorderProvider>
              <WishlistProvider>
              <SiteCopyProvider>
              <ScrollToTop />
              <Header />
              <CartDrawer />
              <PreorderDialog />
              <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="/collections/:slug" element={<Catalog />} />
                <Route path="/catalog" element={<Catalog />} />
                <Route path="/products/preorder/:id" element={<PreorderProductDetail />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/journal" element={<Journal />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/checkout/cancel" element={<CheckoutCancel />} />
                <Route path="/checkout/afterpay/return" element={<AfterpayReturn />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/shopping-with-us" element={<ShoppingWithUs />} />
                <Route path="/interior-styling" element={<InteriorStyling />} />
                <Route path="/showrooms" element={<Showrooms />} />
                <Route path="/refer-a-friend" element={<ReferAFriend />} />
                <Route path="/customisation" element={<Customisation />} />
                <Route path="/sustainability" element={<Sustainability />} />
                <Route path="/ambassador-program" element={<Ambassador />} />
                <Route path="/affiliate-program" element={<Affiliate />} />
                <Route path="/blog" element={<Navigate to="/journal" replace />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              <Footer />
              <Toaster />
              <ConciergeChat />
              </SiteCopyProvider>
              </WishlistProvider>
            </PreorderProvider>
          </CartProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
